<template>
    <seismic-layout>
        <main class="setup-page">
            <section class="header">
                <div class="messaging info">
                    <p>
                        New and Improved Seis Brace Seismic Software with Anvil and AFCON Products. Compliant with NFPA 13-2016 bracing requirements.
                    </p>
                    <p>
                        For questions, please contact your local ASC representative or call <a href="tel:8446926845">844-692-6845</a>.
                    </p>
                    <div class="images">
                        <img src="https://s3.us-east-2.amazonaws.com/asc-es.com/images/logos/asc_anvil_rgb_pos_sm.png" alt="Anvil logo">
                        <img src="https://s3.us-east-2.amazonaws.com/asc-es.com/images/logos/asc_afcon_rgb_pos_sm.png" alt="AFCON">
                        <img src="https://s3.us-east-2.amazonaws.com/asc-es.com/images/certifications/culus.png" alt="UL Listed">
                        <img src="https://s3.us-east-2.amazonaws.com/asc-es.com/images/certifications/fm-approved.png" alt="FM Approved">
                    </div>
                </div>
                <h1>1. Seismic Project Setup</h1>
                <p>Enter information for your Seismic project below.</p>
            </section>

            <loading v-if="loading"></loading>

            <section class="setup">
                <div v-if="!loading">
                    <fieldset>
                        <div class="text">
                            <label class="required" for="project-name">
                                Seismic Project Name
                            </label>
                            <input type="text" v-model="record.name" id="project-name" placeholder="Name your project" required>
                            <seismic-errors :errors="errors" prop="name"></seismic-errors>
                        </div>
                    </fieldset>

                    <fieldset class="flex notes">
                        <label>
                            Product Agency Listings
                        </label>
                        <div>
                            <div>
                                <div class="select">
                                    <select v-model="record.listing">
                                        <option value="fm-approved">FM Approved</option>
                                        <option value="ul-listed">UL Listed</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <strong>Code Requirements</strong>
                                <p class="note">
                                    NFPA 13-2016
                                </p>
                            </div>
                        </div>
                    </fieldset>

                    <fieldset class="setup">
                        <label>Project Units</label>
                        <div class="select">
                            <select v-model="record.useMetric">
                                <option value="0">Imperial (ft, in, lbs, NPS)</option>
                                <option value="1">Metric (m, mm, kg, DN)</option>
                            </select>
                        </div>
                    </fieldset>

                    <fieldset class="flex notes">
                        <label>Max l/r for this Project</label>
                        <div>
                            <div>
                                <div class="select">
                                    <select v-model="record.max">
                                        <option value="100">100</option>
                                        <option value="200">200</option>
                                        <option value="300">300</option>
                                    </select>
                                </div>
                                <seismic-errors :errors="errors" prop="max"></seismic-errors>
                            </div>
                            <div>
                                <p class="note">
                                    Use this section to determine the max l/r for this seismic project submittal. Individual brace l/r values may be reduced later in this Seis Brace software. For typical NFPA 13 applications, a max l/r of 300 is acceptable.
                                </p>
                            </div>
                        </div>
                    </fieldset>

                    <fieldset class="flex notes add-more">
                        <label>Note 1</label>
                        <div>
                            <div class="textarea">
                                <textarea v-model="record.notes1"></textarea>
                            </div>
                            <div>
                                <p class="note">
                                    Use the note section to be specific about any special detailed
                                    information regarding this seismic project submittal.
                                </p>
                            </div>
                        </div>
                    </fieldset>

                    <fieldset class="add-more" v-if="showAdditionalNote">
                        <div class="textarea">
                            <label>Note 2</label>
                            <textarea v-model="record.notes2"></textarea>
                        </div>
                    </fieldset>

                    <div v-if="!showAdditionalNote" class="actions">
                        <button class="simple" @click="toggleAdditionalNote()">
                            <i class="fa fa-plus"></i>
                            Add Additional Note
                        </button>
                    </div>
                </div>
            </section>

            <section v-if="!loading">
                <h1>Contacts</h1>

                <div class="contacts" ref="contacts">
                    <seismic-contact v-for="(contact, index) in contacts" :key="contact.uid" :id="contact.id" v-on:sync-contact="syncContact" v-on:editing-contact="changedContacts=true" v-on:delete-contact="deleteContact" :uid="contact.uid"></seismic-contact>
                </div>

                <div class="actions">
                    <button class="simple" @click="addContact()">
                        <i class="fa fa-plus"></i>
                        Add Contact
                    </button>
                </div>
            </section>

            <section v-if="!loading">
                <seismic-actions
                    @save="saveThenSetup"
                    @next="saveThenNext"
                    :fadeSave="!hasChanges"
                    :save-button-finished-text="saveButtonFinishedText"
                    :next-button-text="nextButtonText"
                    :saving="saving"></seismic-actions>
            </section>

        </main>

        <aside class="primary">
            <seismic-sidebar :design="this.record"></seismic-sidebar>
        </aside>
    </seismic-layout>
</template>

<script>
    import SeismicLayout from './layout';
    import SeismicHeader from './header';
    import SeismicContact from './contact';

    import SeismicErrors from './shared/errors';
    import SeismicSidebar from './shared/sidebar';
    import SeismicActions from './shared/actions';

    import ShortUniqueId from 'short-unique-id';
    import Vue from 'vue';
    import ClockvineMixin from 'vue/common/clockvine-mixin';

    const uid = new ShortUniqueId();


    export default {
        props: [
            'projectId'
        ],

        components: {
            SeismicLayout,
            SeismicHeader,
            SeismicSidebar,
            SeismicContact,
            SeismicActions,
            SeismicErrors,
        },

        mixins: [ClockvineMixin],

        data: function() {
            return {
                type: 'seismicDesign',
                record: this.baseRecord(),
                contacts: [],
                additionalNoteToggled: false,
                savingToggle: false,
                saveButtonFinishedText: 'Saved!',
                changedContacts: false
            };
        },

        mounted() {
            if (this.exists) {
                this.$store.dispatch('seismicDesignContact/index', { seismicDesignId: this.$options.propsData.id })
                    .then((response) => {
                        let contacts = this.$store.state.seismicDesignContact.indexes[`/actions/anvil-module/seismic-design-contact/index?seismicDesignId=${this.id}`];
                        contacts.forEach(c => this.addContact(c));
                        this.changedContacts = false;
                    });

                this.$store.dispatch('seismicDesignBraceConfiguration/reindex', { seismicDesignId: this.$options.propsData.id })
            }
        },

        computed: {
            braces() {
                return this.$store.state.seismicDesignBraceConfiguration.indexes[`/actions/anvil-module/seismic-design-brace-configuration/index?seismicDesignId=${this.id}`] || [];
            },

            hasBraces() {
                return this.braces.length > 0;
            },

            nextButtonText() {
                return this.hasBraces ? 'Save and View Summary' : 'Next';
            },

            showAdditionalNote() {
                return this.additionalNoteToggled || this.record.notes2;
            },
            hasChanges() {
                return this.changedContacts || this.isDirty();
            },
        },

        methods:  {
            toggleAdditionalNote() {
                this.additionalNoteToggled = true;
            },

            baseRecord() {
                return {
                    projectId: this.projectId || null,
                };
            },

            syncContact(data) {
                let index = this.contacts.findIndex(i => i.uid === data.uid);

                this.contacts[index] = data;
            },

            addContact(data = {}) {
                this.changedContacts = true;

                this.contacts.push({
                    id: null,
                    uid: uid.randomUUID(6),
                    ...data
                });
            },

            deleteContact(uid) {
                this.changedContacts = true;

                this.contacts = this.contacts.filter((item) => {
                    return item.uid !== uid;
                });
            },

            getRecordParams() {
                let record = Object.assign({}, this.record, {
                    contacts: this.contacts,
                });

                return Object.assign({}, this.getUrlParams(), { data: record });
            },

            saveThenSetup() {
                this.save().then(() => {
                    this.$router.push({name: 'seismic-setup', params: { id: this.record.id }});
                });
            },

            saveThenNext() {
                this.save().then(() => {
                    if (this.hasBraces) {
                        this.$router.push({name: 'seismic-brace-summary', params: { id: this.record.id }});
                    } else {
                        this.$router.push({name: 'seismic-brace-new', params: { seismicDesignId: this.record.id }});
                    }
                });
            },
        },
    };
</script>
